import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { HELP_CATEGORIES } from "@/data/helpContent";
import { HelpArticlePreview } from "./HelpPreviews";

export function HelpCenter({ open, onOpenChange }) {
  const [categoryKey, setCategoryKey] = useState(null);
  const [articleKey, setArticleKey] = useState(null);

  const category = HELP_CATEGORIES.find((c) => c.key === categoryKey);
  const article = category?.articles.find((a) => a.key === articleKey);

  const handleOpenChange = (next) => {
    onOpenChange(next);
    if (!next) {
      setTimeout(() => {
        setCategoryKey(null);
        setArticleKey(null);
      }, 200);
    }
  };

  const goBack = () => {
    if (article) setArticleKey(null);
    else if (category) setCategoryKey(null);
  };

  const title = article ? article.title : category ? category.title : "Help Center";

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="p-0 w-full sm:max-w-lg flex flex-col gap-0" data-testid="help-center-panel">
        <SheetHeader className="px-5 py-4 border-b border-slate-200 flex-row items-center gap-2 space-y-0 text-left">
          {(category || article) && (
            <button
              data-testid="help-center-back-button"
              onClick={goBack}
              className="text-slate-400 hover:text-slate-700 transition-colors -ml-1 p-1 shrink-0"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <SheetTitle className="text-base font-semibold text-slate-900 truncate">{title}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="flex-1">
          <div className="p-5">
            {!category && (
              <div className="space-y-2" data-testid="help-category-list">
                {HELP_CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`help-category-${c.key}`}
                    onClick={() => setCategoryKey(c.key)}
                    className="w-full flex items-start gap-3 rounded-lg border border-slate-200 p-3.5 text-left hover:border-blue-300 hover:bg-blue-50/50 transition-colors group"
                  >
                    <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                      <c.icon size={16} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{c.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{c.description}</p>
                    </div>
                    <ChevronRight size={15} className="text-slate-300 mt-2.5 shrink-0" />
                  </button>
                ))}
              </div>
            )}

            {category && !article && (
              <div className="space-y-2" data-testid="help-article-list">
                {category.articles.map((a) => (
                  <button
                    key={a.key}
                    data-testid={`help-article-${a.key}`}
                    onClick={() => setArticleKey(a.key)}
                    className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-3.5 text-left hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                  >
                    <span className="text-sm font-medium text-slate-800">{a.title}</span>
                    <ChevronRight size={15} className="text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            )}

            {article && (
              <div className="space-y-5" data-testid="help-article-detail">
                <HelpArticlePreview preview={article.preview} />
                <ol className="space-y-3">
                  {article.steps.map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm text-slate-600 leading-relaxed">
                      <span className="shrink-0 h-5 w-5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                {article.videoUrl && (
                  <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="help-article-video">
                    <iframe src={article.videoUrl} className="w-full aspect-video" allowFullScreen title={article.title} />
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
